// DEBT-VITA™ — Les 8 graphiques du dashboard
// Tous basés sur Recharts (uniformisation), avec tooltips bilingues UM + vies.
import React from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  BarChart,
  Bar,
  ComposedChart,
  Line,
  Treemap,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DEBT_COLORS } from "@/lib/debt";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function fmt(v, digits = 1) {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  return Number(v).toLocaleString("fr-FR", {
    maximumFractionDigits: digits,
    minimumFractionDigits: 0,
  });
}

function fmtMoney(v, currency = "UM") {
  if (v === null || v === undefined) return "—";
  return `${fmt(v, 0)} ${currency}`;
}

function ChartCard({ title, subtitle, testId, children, footer }) {
  return (
    <Card data-testid={testId} className="overflow-hidden">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold">{title}</CardTitle>
        {subtitle ? (
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        ) : null}
      </CardHeader>
      <CardContent className="p-3 pt-0">{children}</CardContent>
      {footer ? (
        <div className="border-t border-border bg-muted/30 px-4 py-2 text-xs text-muted-foreground">
          {footer}
        </div>
      ) : null}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Graph 1 — Gauge "Thermomètre Vitaliste" (semi-circle, custom SVG)
// ---------------------------------------------------------------------------
function VitalistThermometer({ pctCareer }) {
  // pctCareer is e.g. 25.5 (%). Clamp 0-100.
  const v = Math.max(0, Math.min(100, Number(pctCareer) || 0));
  const W = 320;
  const H = 180;
  const cx = W / 2;
  const cy = H - 8;
  const r = 130;
  // Build 4 colored arc paths
  const arcs = [
    { from: 0, to: 10, color: DEBT_COLORS.green, label: "SOUTENABLE" },
    { from: 10, to: 25, color: DEBT_COLORS.yellow, label: "TENSION" },
    { from: 25, to: 50, color: DEBT_COLORS.orange, label: "CRITIQUE" },
    { from: 50, to: 100, color: DEBT_COLORS.red, label: "INSOUTENABLE" },
  ];
  const polar = (deg) => {
    const rad = (Math.PI * deg) / 180;
    return [cx - r * Math.cos(rad), cy - r * Math.sin(rad)];
  };
  const arcPath = (from, to) => {
    const a1 = (from / 100) * 180;
    const a2 = (to / 100) * 180;
    const [x1, y1] = polar(a1);
    const [x2, y2] = polar(a2);
    const largeArc = a2 - a1 > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`;
  };
  const needleAngle = (v / 100) * 180;
  const [nx, ny] = polar(needleAngle);

  return (
    <div className="flex flex-col items-center" data-testid="debt-graph-1-thermometer">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full max-w-sm">
        {arcs.map((a) => (
          <path
            key={a.from}
            d={arcPath(a.from, a.to)}
            stroke={a.color}
            strokeWidth={22}
            fill="none"
            strokeLinecap="butt"
          />
        ))}
        {/* Needle */}
        <line
          x1={cx}
          y1={cy}
          x2={nx}
          y2={ny}
          stroke="#0F1419"
          strokeWidth={3}
          strokeLinecap="round"
          style={{ transition: "all 0.6s cubic-bezier(.34,1.56,.64,1)" }}
        />
        <circle cx={cx} cy={cy} r={6} fill="#0F1419" />
        <text
          x={cx}
          y={cy - 36}
          textAnchor="middle"
          fontSize="22"
          fontWeight="800"
          fill="currentColor"
        >
          {fmt(v, 1)} %
        </text>
        <text
          x={cx}
          y={cy - 18}
          textAnchor="middle"
          fontSize="10"
          fill="#888"
          fontWeight="600"
        >
          DE LA CARRIÈRE
        </text>
      </svg>
      <p className="mt-1 text-center text-xs text-muted-foreground">
        sacrifiée à la dette publique
      </p>
    </div>
  );
}

export function Graph1Thermometer({ results }) {
  return (
    <ChartCard
      title="Thermomètre vitaliste de la dette"
      subtitle="Part de la carrière active aliénée à la dette publique"
      testId="debt-graph-1"
    >
      <VitalistThermometer pctCareer={results?.pct_carriere_dette} />
    </ChartCard>
  );
}

// ---------------------------------------------------------------------------
// Graph 2 — Donut "Anatomie de la dette par créancier"
// ---------------------------------------------------------------------------
const CREDITOR_COLORS = [
  DEBT_COLORS.blue,
  DEBT_COLORS.gold,
  DEBT_COLORS.red,
  DEBT_COLORS.green,
  DEBT_COLORS.orange,
  DEBT_COLORS.yellow,
  "#7F8C8D",
  "#9B59B6",
];

export function Graph2Donut({ results, debtParams }) {
  const currency = debtParams?.currency || "UM";
  const breakdown = results?.creditor_breakdown || [];
  let dataset = [];
  let isFallback = false;
  if (breakdown.length > 0) {
    dataset = breakdown.map((c, i) => ({
      name: c.name || `Créancier ${i + 1}`,
      value: c.amount || 0,
      percent: c.part_pct,
      jours: c.jours_vie,
    }));
  } else if (debtParams) {
    isFallback = true;
    const dom = debtParams.domestic_debt;
    const ext = debtParams.external_debt;
    const tot = debtParams.total_public_debt;
    if (dom !== null && dom !== undefined) {
      dataset.push({ name: "Dette intérieure", value: dom, percent: tot ? (dom / tot) * 100 : null });
    }
    if (ext !== null && ext !== undefined) {
      dataset.push({ name: "Dette extérieure", value: ext, percent: tot ? (ext / tot) * 100 : null });
    }
  }

  if (dataset.length === 0) {
    return (
      <ChartCard
        title="Anatomie de la dette par créancier"
        subtitle="Aucun créancier saisi — section optionnelle du formulaire."
        testId="debt-graph-2"
      >
        <div className="py-8 text-center text-sm text-muted-foreground">
          Renseignez la section <strong>D — Détail par créancier</strong> ou
          décomposez la dette intérieure/extérieure pour activer ce graphique.
        </div>
      </ChartCard>
    );
  }

  return (
    <ChartCard
      title="Anatomie de la dette par créancier"
      subtitle={isFallback ? "Décomposition intérieure / extérieure" : `${dataset.length} créancier(s) saisis`}
      testId="debt-graph-2"
    >
      <ResponsiveContainer width="100%" height={260}>
        <PieChart>
          <Pie
            data={dataset}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={55}
            outerRadius={90}
            paddingAngle={2}
          >
            {dataset.map((_, i) => (
              <Cell key={i} fill={CREDITOR_COLORS[i % CREDITOR_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip
            formatter={(value, name, p) => {
              const pct = p?.payload?.percent;
              const jvd = p?.payload?.jours;
              return [
                `${fmtMoney(value, currency)}${pct !== null && pct !== undefined ? ` (${fmt(pct, 1)} %)` : ""}${jvd !== null && jvd !== undefined ? ` — ${fmt(jvd, 1)} j vie/tête` : ""}`,
                name,
              ];
            }}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
        </PieChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

// ---------------------------------------------------------------------------
// Graph 3 — Calendrier de vie dette (365 cases)
// ---------------------------------------------------------------------------
export function Graph3LifeCalendar({ results }) {
  const debtDays = Math.max(0, Math.round(results?.jours_vie_service_annuel || 0));
  const fiscalDays = Math.max(0, Math.round(results?.jours_vie_fiscalite || 0));
  // Cap at 365
  const debtCap = Math.min(debtDays, 365);
  const fiscalCap = Math.min(fiscalDays, 365 - debtCap);
  const personalCap = Math.max(0, 365 - debtCap - fiscalCap);
  const cells = [];
  for (let i = 0; i < debtCap; i++) cells.push("debt");
  for (let i = 0; i < fiscalCap; i++) cells.push("fiscal");
  for (let i = 0; i < personalCap; i++) cells.push("self");

  const liberationDay = debtDays;
  const liberationDate = liberationDay > 0
    ? new Date(new Date().getFullYear(), 0, liberationDay).toLocaleDateString("fr-FR", {
        day: "numeric",
        month: "long",
      })
    : null;

  return (
    <ChartCard
      title="Calendrier de vie : Dette / Fiscalité / Soi-même"
      subtitle="Chaque case = 1 jour travaillé sur l'année"
      testId="debt-graph-3"
      footer={
        liberationDate
          ? `🗓 Jour de libération de la dette : ${liberationDate} (${liberationDay}ᵉ jour de l'année)`
          : "Aucun service de la dette saisi."
      }
    >
      <div className="grid grid-cols-[repeat(auto-fill,minmax(8px,1fr))] gap-[2px]">
        {cells.map((kind, i) => {
          const color =
            kind === "debt"
              ? "bg-black"
              : kind === "fiscal"
              ? "bg-blue-600"
              : "bg-emerald-500";
          return (
            <div
              key={i}
              className={`h-2.5 w-full rounded-sm ${color}`}
              title={
                kind === "debt"
                  ? `Jour ${i + 1} — Dette`
                  : kind === "fiscal"
                  ? `Jour ${i + 1} — Fiscalité`
                  : `Jour ${i + 1} — Soi-même`
              }
            />
          );
        })}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3 text-[11px]">
        <span className="inline-flex items-center gap-1">
          <span className="h-2.5 w-2.5 rounded-sm bg-black" /> Dette ({debtCap} j)
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="h-2.5 w-2.5 rounded-sm bg-blue-600" /> Fiscalité ({fiscalCap} j)
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="h-2.5 w-2.5 rounded-sm bg-emerald-500" /> Soi-même ({personalCap} j)
        </span>
      </div>
    </ChartCard>
  );
}

// ---------------------------------------------------------------------------
// Graph 4 — Projection 3 scénarios
// ---------------------------------------------------------------------------
export function Graph4Projection({ results, debtParams }) {
  const currency = debtParams?.currency || "UM";
  const sc = results?.projection_scenarios;
  if (!sc || !sc.trend || sc.trend.length === 0) {
    return (
      <ChartCard
        title="Projection de la dette en vies"
        subtitle="3 scénarios sur l'horizon choisi"
        testId="debt-graph-4"
      >
        <div className="py-8 text-center text-sm text-muted-foreground">
          Données de projection non disponibles.
        </div>
      </ChartCard>
    );
  }

  // Merge by year
  const byYear = new Map();
  const merge = (rows, key) => {
    rows.forEach((r) => {
      if (!byYear.has(r.annee)) byYear.set(r.annee, { annee: r.annee });
      byYear.get(r.annee)[key] = r.dette_totale;
      byYear.get(r.annee)[key + "_avd"] = r.annees_vie;
    });
  };
  merge(sc.optimist || [], "optimist");
  merge(sc.trend || [], "trend");
  merge(sc.pessimist || [], "pessimist");
  const dataset = Array.from(byYear.values()).sort((a, b) => a.annee - b.annee);

  return (
    <ChartCard
      title="Projection de la dette en vies"
      subtitle="Optimiste / Tendanciel / Pessimiste"
      testId="debt-graph-4"
    >
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart data={dataset} margin={{ left: 4, right: 4 }}>
          <defs>
            <linearGradient id="trendArea" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={DEBT_COLORS.red} stopOpacity={0.5} />
              <stop offset="95%" stopColor={DEBT_COLORS.red} stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#aaa" strokeOpacity={0.2} />
          <XAxis dataKey="annee" tick={{ fontSize: 11 }} />
          <YAxis
            tick={{ fontSize: 10 }}
            tickFormatter={(v) =>
              v >= 1e9 ? `${(v / 1e9).toFixed(1)}Md` : v >= 1e6 ? `${(v / 1e6).toFixed(0)}M` : v
            }
          />
          <Tooltip
            formatter={(value, name) => [fmtMoney(value, currency), name]}
            labelFormatter={(l) => `Année ${l}`}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Area
            type="monotone"
            dataKey="trend"
            name="Tendanciel"
            stroke={DEBT_COLORS.red}
            fill="url(#trendArea)"
            strokeWidth={2}
          />
          <Area
            type="monotone"
            dataKey="optimist"
            name="Optimiste"
            stroke={DEBT_COLORS.green}
            fill="transparent"
            strokeDasharray="5 5"
            strokeWidth={2}
          />
          <Area
            type="monotone"
            dataKey="pessimist"
            name="Pessimiste"
            stroke="#0F1419"
            fill="transparent"
            strokeDasharray="2 4"
            strokeWidth={2}
          />
        </AreaChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

// ---------------------------------------------------------------------------
// Graph 5 — Charge intergénérationnelle
// ---------------------------------------------------------------------------
export function Graph5Generations({ results, debtParams }) {
  const proj = results?.projection_data || [];
  const horizonYears = debtParams?.projection_years || 30;
  if (proj.length === 0) {
    return (
      <ChartCard
        title="Charge intergénérationnelle"
        subtitle="Années de vie hypothéquées par génération"
        testId="debt-graph-5"
      >
        <div className="py-8 text-center text-sm text-muted-foreground">
          Données de projection insuffisantes.
        </div>
      </ChartCard>
    );
  }
  // 3 generations: actuelle (offset 0), -18 ans (offset 18 si présent),
  // -36 ans (offset 36 si présent) — sinon dernière disponible
  const get = (offset) => {
    const idx = Math.min(offset, proj.length - 1);
    return proj[idx];
  };
  const cur = proj[0] || {};
  const teen = get(Math.min(18, proj.length - 1));
  const child = get(proj.length - 1);

  const dataset = [
    {
      generation: "Active aujourd'hui",
      depart: 0,
      fin: cur.annees_vie || 0,
    },
    {
      generation: `−18 ans (active en ~${new Date().getFullYear() + 18})`,
      depart: cur.annees_vie || 0,
      fin: teen.annees_vie || 0,
    },
    {
      generation: `Né aujourd'hui (active en ~${new Date().getFullYear() + horizonYears})`,
      depart: teen.annees_vie || 0,
      fin: child.annees_vie || 0,
    },
  ];

  const childYears = child.annees_vie || 0;

  return (
    <ChartCard
      title="Charge intergénérationnelle"
      subtitle="Années de vie hypothéquées au démarrage et en fin de carrière (par génération)"
      testId="debt-graph-5"
      footer={
        childYears > 0
          ? `Un enfant né aujourd'hui débutera sa vie active avec ${fmt(
              teen.annees_vie || 0,
              2
            )} années déjà hypothéquées par des décisions qu'il n'a pas prises.`
          : null
      }
    >
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={dataset} margin={{ left: 4, right: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#aaa" strokeOpacity={0.2} />
          <XAxis dataKey="generation" tick={{ fontSize: 10 }} />
          <YAxis
            tick={{ fontSize: 10 }}
            tickFormatter={(v) => `${fmt(v, 1)} ans`}
          />
          <Tooltip formatter={(v) => `${fmt(v, 3)} ans`} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar dataKey="depart" name="Démarrage carrière" fill={DEBT_COLORS.blue} />
          <Bar dataKey="fin" name="Fin carrière (projetée)" fill={DEBT_COLORS.red} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

// ---------------------------------------------------------------------------
// Graph 6 — Dette vs Fiscalité (horizontal bar)
// ---------------------------------------------------------------------------
export function Graph6DebtVsFiscality({ results }) {
  const fiscDays = results?.jours_vie_fiscalite;
  const debtDays = results?.jours_vie_dette;
  const tavd = results?.tavd;
  if (!fiscDays && !debtDays) {
    return (
      <ChartCard
        title="Dette vs Fiscalité"
        subtitle="Comparaison vitaliste"
        testId="debt-graph-6"
      >
        <div className="py-8 text-center text-sm text-muted-foreground">
          Données fiscales manquantes (lance d'abord une analyse vitaliste principale).
        </div>
      </ChartCard>
    );
  }
  const dataset = [
    { kind: "Fiscalité (1 an)", jours: fiscDays || 0, color: DEBT_COLORS.blue },
    { kind: "Dette totale", jours: debtDays || 0, color: "#0F1419" },
  ];
  return (
    <ChartCard
      title="Dette vs Fiscalité — comparaison vitaliste"
      subtitle="Jours de vie : un an de fiscalité face à la dette accumulée"
      testId="debt-graph-6"
      footer={
        tavd !== null && tavd !== undefined
          ? `TAVD = ${fmt(tavd, 1)} % — la dette représente ${fmt(tavd / 100, 2)}× la pression fiscale annuelle en vies humaines.`
          : null
      }
    >
      <ResponsiveContainer width="100%" height={180}>
        <BarChart layout="vertical" data={dataset} margin={{ left: 10, right: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#aaa" strokeOpacity={0.2} />
          <XAxis type="number" tick={{ fontSize: 10 }} />
          <YAxis dataKey="kind" type="category" tick={{ fontSize: 11 }} width={120} />
          <Tooltip formatter={(v) => `${fmt(v, 1)} jours de vie`} />
          <Bar dataKey="jours" name="Jours de vie">
            {dataset.map((d, i) => (
              <Cell key={i} fill={d.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

// ---------------------------------------------------------------------------
// Graph 7 — Service de la dette : intérêts vs capital + taux
// ---------------------------------------------------------------------------
export function Graph7ServiceComposition({ results, debtParams }) {
  const interest = results?.jours_vie_interets;
  const principal = results?.jours_vie_capital;
  const rate = debtParams?.average_interest_rate;
  if (
    (interest === null || interest === undefined) &&
    (principal === null || principal === undefined)
  ) {
    return (
      <ChartCard
        title="Service de la dette : intérêts vs capital"
        subtitle="Décomposition annuelle"
        testId="debt-graph-7"
      >
        <div className="py-8 text-center text-sm text-muted-foreground">
          Renseignez les champs <em>Intérêts annuels</em> et{" "}
          <em>Remboursements capital</em> du formulaire.
        </div>
      </ChartCard>
    );
  }
  const dataset = [
    {
      label: `Année ${debtParams?.fiscal_year || new Date().getFullYear()}`,
      interets: interest || 0,
      capital: principal || 0,
      taux: rate || 0,
    },
  ];
  return (
    <ChartCard
      title="Service de la dette : intérêts vs capital"
      subtitle="Jours de vie/an + taux d'intérêt moyen"
      testId="debt-graph-7"
    >
      <ResponsiveContainer width="100%" height={260}>
        <ComposedChart data={dataset}>
          <CartesianGrid strokeDasharray="3 3" stroke="#aaa" strokeOpacity={0.2} />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
          <YAxis
            yAxisId="left"
            tick={{ fontSize: 10 }}
            tickFormatter={(v) => `${fmt(v, 1)} j`}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={{ fontSize: 10 }}
            tickFormatter={(v) => `${fmt(v, 1)} %`}
          />
          <Tooltip
            formatter={(v, name) =>
              name === "Taux moyen" ? `${fmt(v, 2)} %` : `${fmt(v, 2)} j vie/an`
            }
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar
            yAxisId="left"
            dataKey="interets"
            stackId="svc"
            name="Intérêts"
            fill={DEBT_COLORS.red}
          />
          <Bar
            yAxisId="left"
            dataKey="capital"
            stackId="svc"
            name="Capital"
            fill={DEBT_COLORS.blue}
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="taux"
            name="Taux moyen"
            stroke={DEBT_COLORS.gold}
            strokeWidth={3}
            dot={{ r: 5, fill: DEBT_COLORS.gold }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

// ---------------------------------------------------------------------------
// Graph 8 — Treemap répartition vitaliste
// ---------------------------------------------------------------------------
export function Graph8Treemap({ results, debtParams }) {
  const dom = debtParams?.domestic_debt;
  const ext = debtParams?.external_debt;
  const interest = results?.jours_vie_interets;
  const dailySalary = results?.daily_salary_used;
  const activePop = results?.active_population_used;

  const blocks = [];
  if (dom && dailySalary && activePop) {
    blocks.push({
      name: "Dette intérieure",
      size: (dom / activePop) / dailySalary,
      um: dom,
      kind: "interior",
    });
  }
  if (ext && dailySalary && activePop) {
    blocks.push({
      name: "Dette extérieure",
      size: (ext / activePop) / dailySalary,
      um: ext,
      kind: "exterior",
    });
  }
  if (interest) {
    blocks.push({
      name: "Service intérêts/an",
      size: interest,
      um: debtParams?.interest_payments,
      kind: "interest",
    });
  }
  if (blocks.length === 0) {
    return (
      <ChartCard
        title="Répartition vitaliste intérieure / extérieure / intérêts"
        subtitle="Treemap par poids en vies"
        testId="debt-graph-8"
      >
        <div className="py-8 text-center text-sm text-muted-foreground">
          Renseignez la dette intérieure et extérieure, ou les intérêts annuels.
        </div>
      </ChartCard>
    );
  }
  const totalSize = blocks.reduce((s, b) => s + b.size, 0) || 1;
  // Color from green (small) to red (large)
  const fillFor = (size) => {
    const ratio = size / totalSize;
    if (ratio < 0.2) return DEBT_COLORS.green;
    if (ratio < 0.4) return DEBT_COLORS.yellow;
    if (ratio < 0.7) return DEBT_COLORS.orange;
    return DEBT_COLORS.red;
  };

  return (
    <ChartCard
      title="Répartition vitaliste intérieure / extérieure / intérêts"
      subtitle="Taille des blocs = poids en jours de vie"
      testId="debt-graph-8"
    >
      <ResponsiveContainer width="100%" height={260}>
        <Treemap
          data={blocks.map((b) => ({ ...b, fill: fillFor(b.size) }))}
          dataKey="size"
          nameKey="name"
          stroke="#fff"
          content={(props) => {
            const { x, y, width, height, name, fill, size, um } = props;
            if (width < 30 || height < 24) return null;
            return (
              <g>
                <rect x={x} y={y} width={width} height={height} fill={fill} stroke="#fff" />
                <text
                  x={x + 6}
                  y={y + 16}
                  fill="#fff"
                  fontSize={11}
                  fontWeight="700"
                >
                  {name}
                </text>
                <text x={x + 6} y={y + 32} fill="#fff" fontSize={10}>
                  {fmt(size, 1)} j vie
                </text>
                {um ? (
                  <text x={x + 6} y={y + 46} fill="#fff" fontSize={9} opacity={0.85}>
                    {fmtMoney(um, debtParams?.currency || "UM")}
                  </text>
                ) : null}
              </g>
            );
          }}
        />
      </ResponsiveContainer>
    </ChartCard>
  );
}
